import { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useLocalStorage } from "@uidotdev/usehooks";

import { allNavItems, MainNavItemConfig } from "../config/mainNavConfig";

const isSectionActive = (
  item: MainNavItemConfig,
  pathname: string,
): boolean => {
  if (item.isActive?.(pathname)) {
    return true;
  }

  return (
    item.children?.some((child) =>
      child.isActive ? child.isActive(pathname) : pathname.startsWith(child.to),
    ) ?? false
  );
};

export const useExpandedNavSections = () => {
  const { pathname } = useLocation();
  const [expandedIds, setExpandedIds] = useLocalStorage<string[]>(
    "sidebar-expanded-sections",
    [],
  );

  useEffect(() => {
    const activeSectionIds = allNavItems
      .filter(
        (item) => item.children?.length && isSectionActive(item, pathname),
      )
      .map((item) => item.id);

    if (activeSectionIds.length === 0) {
      return;
    }

    setExpandedIds((prev) => {
      if (activeSectionIds.every((id) => prev.includes(id))) {
        return prev;
      }

      return Array.from(new Set([...prev, ...activeSectionIds]));
    });
  }, [pathname, setExpandedIds]);

  const isExpanded = useCallback(
    (id: string) => expandedIds.includes(id),
    [expandedIds],
  );

  const toggleExpanded = useCallback(
    (id: string) => {
      setExpandedIds((prev) =>
        prev.includes(id)
          ? prev.filter((sectionId) => sectionId !== id)
          : [...prev, id],
      );
    },
    [setExpandedIds],
  );

  return { isExpanded, toggleExpanded };
};
